import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Brain, Trophy, Clock, BarChart2 } from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import MultiplayerGame from "../../components/MultiplayerGame";
import PvPLobby from "../../components/PvPLobby";

const ColorClashMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="color-clash" // IMPORTANT: Change this for each game!
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "color-clash", name: "Color Clash Challenge" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={ColorClashMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const ColorClashMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("play"); // "play" | "results"
  const [difficulty, setDifficulty] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Trials
  const [trials, setTrials] = useState([]);
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);
  const [countdown, setCountdown] = useState(null); // For 3-2-1 countdown
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Stimulus
  const [currentWord, setCurrentWord] = useState("");
  const [currentColor, setCurrentColor] = useState("");
  const [hasResponded, setHasResponded] = useState(false);

  // Scoring & Performance
  const [score, setScore] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [congruentCount, setCongruentCount] = useState(0);
  const [incongruentCount, setIncongruentCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [congruentRTs, setCongruentRTs] = useState([]);
  const [incongruentRTs, setIncongruentRTs] = useState([]);

  // Timing references
  const goSignalTimeRef = useRef(null);

  // Define color & word sets
  const colorOptions = ["red", "green", "blue", "yellow", "purple"];
  const wordOptions = ["RED", "GREEN", "BLUE", "YELLOW", "PURPLE"];

  // ----------------------------------
  //        MULTIPLAYER STATE
  // ----------------------------------
  useEffect(() => {
    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        if (!gameStarted && !showCountdown) {
          // Both players are ready, start the countdown
          startGame();
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        if (!gameStarted) {
          setGameStarted(true);
          startGame();
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        if (isPlaying) {
          endGame();
        }
      }

      // Check if opponent is ready (for UI indication)
      if (gameState.playerReady && opponent) {
        const opponentReady = gameState.playerReady[opponent.email] || false;
        console.log(`Opponent ready: ${opponentReady}`);
      }
    }
  }, [gameState, gameStarted, isPlaying, opponent, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({
        status: "ready",
      });
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // ----------------------------------
  //        GAME & TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isPlaying]);

  // Start the game with countdown
  const startGame = () => {
    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setReactionTimes([]);
    setCongruentCount(0);
    setIncongruentCount(0);
    setCorrectCount(0);
    setIncorrectCount(0);
    setCongruentRTs([]);
    setIncongruentRTs([]);

    const newTrials = generateTrials(getTrialCountForDifficulty(difficulty));
    setTrials(newTrials);
    setCurrentTrialIndex(0);

    // Set time limit
    setTimeLeft(60);
    setIsPlaying(true);

    // Display the first trial
    if (newTrials.length > 0) {
      const firstTrial = newTrials[0];
      setCurrentWord(firstTrial.word);
      setCurrentColor(firstTrial.color);
      setHasResponded(false);
      goSignalTimeRef.current = Date.now();
    }

    // Update the game state
    if (updateGameState) {
      updateGameState({
        status: "active",
        playerReady: true,
        myProgress: 0,
        myScore: 0,
      });
    }
  };

  // End the game
  const endGame = () => {
    if (scoreSubmitted) return;
    setScoreSubmitted(true);

    setIsPlaying(false);
    setGameMode("results");

    // Submit the score + detailed stats
    if (submitScore) {
      // Create a stats object with all the performance metrics
      const stats = {
        correctCount,
        incorrectCount,
        congruentCount,
        incongruentCount,
        avgCongruentRT:
          congruentRTs.length > 0
            ? Math.round(
                congruentRTs.reduce((a, b) => a + b, 0) / congruentRTs.length
              )
            : 0,
        avgIncongruentRT:
          incongruentRTs.length > 0
            ? Math.round(
                incongruentRTs.reduce((a, b) => a + b, 0) /
                  incongruentRTs.length
              )
            : 0,
        gameLength: 60 - timeLeft, // Assuming 60 second games
        timestamp: new Date(),
      };

      // Submit both score and stats
      submitScore(score, stats);

      // Also update the game state one last time with final score and progress
      if (updateGameState) {
        updateGameState({
          myProgress: 1, // 100% progress
          myScore: score,
          myLastResponse: {
            gameComplete: true,
            finalStats: stats,
          },
        });
      }
    }
  };

  useEffect(() => {
    // Check if the opponent has quit
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      // If we're still playing, end the game automatically with a win
      if (isPlaying) {
        endGame();
        // You could optionally show a special message:
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email, isPlaying]);

  // Generate trials
  const generateTrials = (numTrials) => {
    const trialsArray = [];
    for (let i = 0; i < numTrials; i++) {
      const word = wordOptions[Math.floor(Math.random() * wordOptions.length)];
      const color =
        colorOptions[Math.floor(Math.random() * colorOptions.length)];

      // ~50% chance of forcing an incongruent trial
      const forceIncongruent = Math.random() < 0.5;
      if (!forceIncongruent) {
        // Congruent
        trialsArray.push({ word, color: word.toLowerCase() });
      } else {
        // Incongruent: ensure color != word
        let newColor = color;
        while (newColor.toUpperCase() === word) {
          newColor =
            colorOptions[Math.floor(Math.random() * colorOptions.length)];
        }
        trialsArray.push({ word, color: newColor });
      }
    }
    return trialsArray;
  };

  // Decide how many trials
  const getTrialCountForDifficulty = (lvl) => {
    if (lvl === 1) return 20;
    if (lvl === 2) return 30;
    return 40;
  };

  // Move to next trial
  const nextTrial = (index, trialList) => {
    if (!isPlaying) return;
    if (index >= trialList.length) {
      endGame();
      return;
    }
    setHasResponded(false);

    const current = trialList[index];
    setCurrentWord(current.word);
    setCurrentColor(current.color);

    setCurrentTrialIndex(index);
    goSignalTimeRef.current = Date.now();
  };

  // Format time
  const formatTime = (sec) => {
    const mins = Math.floor(sec / 60);
    const secs = sec % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  // ----------------------------------
  //       HANDLE USER RESPONSE
  // ----------------------------------
  const handleColorResponse = (chosenColor) => {
    if (hasResponded || !isPlaying) return;
    setHasResponded(true);

    const rt = Date.now() - goSignalTimeRef.current;
    setReactionTimes((prev) => [...prev, rt]);

    const trial = trials[currentTrialIndex];
    const isCongruent = trial.word.toLowerCase() === trial.color;
    const isCorrect = chosenColor === trial.color;

    if (isCongruent) {
      setCongruentCount((prev) => prev + 1);
      setCongruentRTs((prev) => [...prev, rt]);
    } else {
      setIncongruentCount((prev) => prev + 1);
      setIncongruentRTs((prev) => [...prev, rt]);
    }

    if (isCorrect) {
      // Reward more for incongruent
      const earnedPoints = isCongruent ? 10 : 15;
      setScore((prev) => prev + earnedPoints);
      setCorrectCount((prev) => prev + 1);

      // Update the game state
      if (updateGameState) {
        updateGameState({
          myProgress: (currentTrialIndex + 1) / trials.length,
          myScore: score + earnedPoints,
          myLastResponse: {
            correct: true,
            points: earnedPoints,
            isCongruent,
          },
        });
      }
    } else {
      // Penalty for errors
      setScore((prev) => (prev > 0 ? prev - 5 : 0));
      setIncorrectCount((prev) => prev + 1);

      // Update the game state
      if (updateGameState) {
        updateGameState({
          myProgress: (currentTrialIndex + 1) / trials.length,
          myScore: score > 0 ? score - 5 : 0,
          myLastResponse: {
            correct: false,
            points: -5,
            isCongruent,
          },
        });
      }
    }

    // Move to next trial after a short delay
    setTimeout(() => {
      nextTrial(currentTrialIndex + 1, trials);
    }, 400);
  };

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="w-full text-white p-0">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-emerald-300 font-medium flex items-center gap-2">
            <Brain size={18} className="text-emerald-300" />
            <span>Color Clash Challenge</span>
          </div>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {/* Start button or waiting indicator */}
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  {waitingForOpponent ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 border-4 border-t-transparent border-emerald-500 rounded-full animate-spin"></div>
                      <p className="text-white text-xl">
                        Waiting for opponent...
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleReadyClick}
                      className="py-3 px-6 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg"
                    >
                      Ready to Play
                    </button>
                  )}
                </div>
              )}

              {/* Countdown overlay */}
              {showCountdown && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <div className="flex flex-col items-center">
                    <div className="text-6xl font-bold text-white mb-4">
                      {countdown > 0 ? countdown : "GO!"}
                    </div>
                    <div className="text-xl text-slate-300">Get ready...</div>
                  </div>
                </div>
              )}

              {/* Game stats header */}
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-purple-500/20 to-indigo-600/20 px-4 py-2 rounded-lg">
                  <span className="text-purple-200 font-medium">
                    Difficulty: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-purple-500/20 to-indigo-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Opponent progress */}
              {gameState && opponent && (
                <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-slate-300">
                      {opponent.name}'s Progress
                    </span>
                    <span className="text-xs text-white">
                      {gameState?.progress && gameState.progress[opponent.email]
                        ? `${Math.round(
                            gameState.progress[opponent.email] * 100
                          )}%`
                        : "0%"}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="bg-purple-500 h-full rounded-full"
                      style={{
                        width: `${
                          ((gameState?.progress &&
                            gameState.progress[opponent.email]) ||
                            0) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                  {gameState?.scores &&
                    gameState.scores[opponent.email] !== undefined && (
                      <div className="mt-1 text-right text-xs text-purple-300">
                        Score: {gameState.scores[opponent.email]}
                      </div>
                    )}
                </div>
              )}

              {/* Game content */}
              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Stimulus display */}
                <div className="relative flex flex-col items-center justify-center w-64 h-64 bg-slate-900/60 border border-slate-700 rounded-xl">
                  <AnimatePresence mode="wait">
                    {currentWord && (
                      <motion.div
                        key={`${currentTrialIndex}-${currentWord}-${currentColor}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                        className="text-4xl font-bold"
                        style={{ color: currentColor }}
                      >
                        {currentWord}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Controls & progress */}
                <div className="md:w-1/3 space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Trial Progress
                    </h3>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-sm">Trial</span>
                      <span className="text-white font-medium">
                        {currentTrialIndex + 1}/{trials.length}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-600"
                        style={{
                          width: `${
                            (currentTrialIndex / trials.length) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Color response buttons - compact layout */}
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <button
                      onClick={() => handleColorResponse("red")}
                      className="py-4 px-6 text-base font-semibold rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Red
                    </button>
                    <button
                      onClick={() => handleColorResponse("green")}
                      className="py-4 px-6 text-base font-semibold rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Green
                    </button>
                    <button
                      onClick={() => handleColorResponse("blue")}
                      className="py-4 px-6 text-base font-semibold rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Blue
                    </button>
                    <button
                      onClick={() => handleColorResponse("yellow")}
                      className="py-4 px-6 text-base font-semibold rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Yellow
                    </button>
                    <button
                      onClick={() => handleColorResponse("purple")}
                      className="col-span-2 py-4 px-6 text-base font-semibold rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Purple
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You scored {score} points in your multiplayer match!
                </p>
              </div>

              {/* Match results comparison */}
              {gameState && gameState.status === "completed" && opponent && (
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-bold text-white text-center mb-4">
                    Match Results
                  </h3>

                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {user?.firstName || user?.email?.split("@")[0]}
                      </div>
                      <div className="text-3xl font-bold text-purple-300 mt-2">
                        {score}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>

                    <div className="flex flex-col items-center px-4">
                      <div className="text-xl font-bold text-white">VS</div>
                      <div className="w-px h-16 bg-slate-600 my-2"></div>
                    </div>

                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {opponent.name}
                      </div>
                      <div className="text-3xl font-bold text-purple-300 mt-2">
                        {gameState.scores?.[opponent.email] || 0}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {score > (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-2 px-4 rounded-lg font-medium">
                        You Won! 🏆
                      </div>
                    ) : score === (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-amber-500/20 border border-amber-500/30 text-amber-300 py-2 px-4 rounded-lg font-medium">
                        It's a Tie! 🤝
                      </div>
                    ) : (
                      <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 py-2 px-4 rounded-lg font-medium">
                        {opponent.name} Won! Better luck next time!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Explanation box */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-purple-300">
                    Congruent Trials
                  </span>
                  : Word & color matched; typically easier/faster.
                  <br />
                  <span className="font-medium text-purple-300">
                    Incongruent Trials
                  </span>
                  : Word & color conflicted; tests inhibitory control.
                  <br />
                  <span className="font-medium text-purple-300">
                    Stroop Interference
                  </span>
                  : Difference in reaction times (incongruent vs. congruent).
                  Lower is better!
                </p>
              </div>

              {/* Performance stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-purple-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-purple-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct Answers</span>
                      <span className="text-white">{correctCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Incorrect Answers</span>
                      <span className="text-red-300">{incorrectCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Congruent Trials</span>
                      <span className="text-white">{congruentCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Incongruent Trials</span>
                      <span className="text-white">{incongruentCount}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-purple-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Reaction times */}
                    <div>
                      <p className="text-sm text-slate-300 mb-1">
                        Average Reaction Times
                      </p>
                      <div className="flex justify-between text-sm text-slate-300">
                        <span>Overall</span>
                        <span>
                          {reactionTimes.length > 0
                            ? `${Math.round(
                                reactionTimes.reduce((a, b) => a + b, 0) /
                                  reactionTimes.length
                              )} ms`
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300">
                      <span>Congruent</span>
                      <span>
                        {congruentRTs.length > 0
                          ? `${Math.round(
                              congruentRTs.reduce((a, b) => a + b, 0) /
                                congruentRTs.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300 mb-2">
                      <span>Incongruent</span>
                      <span>
                        {incongruentRTs.length > 0
                          ? `${Math.round(
                              incongruentRTs.reduce((a, b) => a + b, 0) /
                                incongruentRTs.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="border-t border-slate-700 pt-2 text-sm text-slate-300 flex justify-between">
                      <span>Stroop Interference</span>
                      <span>
                        {incongruentRTs.length > 0 && congruentRTs.length > 0
                          ? `${
                              Math.round(
                                incongruentRTs.reduce((a, b) => a + b, 0) /
                                  incongruentRTs.length
                              ) -
                              Math.round(
                                congruentRTs.reduce((a, b) => a + b, 0) /
                                  congruentRTs.length
                              )
                            } ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="mt-4">
                      <p className="text-slate-300 text-sm leading-relaxed">
                        A lower interference means stronger impulse control and
                        selective attention. Keep practicing to reduce that gap!
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return to lobby button */}
              <div className="flex justify-center">
                <button
                  onClick={() => submitScore(score)}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Return to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorClashMultiWrapper;
